// @ts-check
/* global module */
'use_strict';

/**
 * Manages the site wide banner position and visibility.
 *
 * @property {int} last_known_scroll_position used during scrolling so we know when to hide or show the banner.
 * @property {boolean} animating used to track if the browser is rending a frame during scrolling.
 * @property {int} banner_id used to check if the previously closed banner was this banner.
 * @property {Dictionary<string, HTMLElement>} elements we need to interact with.
 * @property {boolean} closed indicates that the site wide banner was closed by the user.
 */
export class SiteWideBannerView {

    last_known_scroll_position: number = 0;
    animating: boolean = false;
    banner_id: string;
    elements: { [key: string]: HTMLElement } = {};
    closed: boolean;

    /**
     * Instantiate the class, default values and bind to the elements.
     * Check if the user has closed the banner before, then set the initial anchor state.
     */
    constructor() {
        this.banner_id = (<HTMLInputElement>document.getElementById('js-site-wide-banner-id')).value;

        this.bind();

        this.closed = this.is_closed();

        this.anchor_banner();

        const bottom_checkout_buttons = document.querySelector('.ty-cart-content__bottom-buttons');
        if (bottom_checkout_buttons) {
            this.adjust_bottom_checkout_buttons(bottom_checkout_buttons);
        }

        // Set timeout so back to top button can load first.
        setTimeout(this.adjust_back_to_top.bind(this), 0);
    }

    /**
     * Adjusts Back to Top button position to account for Bottom Banner
     * 
     * @returns {void}
     *  */
     adjust_back_to_top(): void {
        const bottom_banner = document.getElementById('js-site-wide-banner');
        const bottom_banner_close_btn = document.getElementById('js-site-wide-banner-close');
        let bottom_banner_height = 0;
        const back_to_top_btn = document.getElementsByClassName('ab__scroll_to_top_button')[0];

        // Make room for chat head if we are on mobile.
        window.addEventListener('load', (e) => {
            const fc_chat_icon = document.getElementById('fc_frame');
            const banner_contents = document.getElementsByClassName('tv-site-wide-banner-contents')[0];

            if (bottom_banner && fc_chat_icon && window.innerWidth <= 767) {
                banner_contents.style.paddingRight = '25px';
            }
        });

        // Set initial back-to-top position
        if (bottom_banner && bottom_banner_close_btn.style.display == 'block' && back_to_top_btn) {
            bottom_banner_height = bottom_banner.clientHeight + 10;
            back_to_top_btn.style.bottom = bottom_banner_height + 'px';
        }

        // If banner is closed, return back-to-top to original position
        bottom_banner_close_btn.addEventListener('click', function(){
            back_to_top_btn.style.bottom = 0 + 'px';
        });

        // Adjust back-to-top position when the user scrolls below the bottom banner's on page position
        if (back_to_top_btn) {
            window.onscroll = function () {
                if (bottom_banner_close_btn.style.display == 'none') {
                    back_to_top_btn.style.bottom = 0 + 'px';
                } else {
                    bottom_banner_height = bottom_banner.clientHeight + 10;
                    back_to_top_btn.style.bottom = bottom_banner_height + 'px';
                }
            }
        }
    }

    /**
     * Adjusts lower checkout buttons position to account for Bottom Banner
     * 
     * @returns {void}
     */
    adjust_bottom_checkout_buttons(bottom_checkout_buttons): void {
        const banner_anchor = document.getElementById('js-site-wide-banner-anchor');
        const bottom_banner = document.getElementById('js-site-wide-banner');
        const bottom_banner_close_btn = document.getElementById('js-site-wide-banner-close');
        const freshdesk_icon = document.getElementById('freshdesk-messaging-icon');

        if (bottom_banner && banner_anchor.classList.contains('fixed')) {
            bottom_checkout_buttons.style.bottom = bottom_banner.offsetHeight + "px";
        }

        bottom_banner_close_btn.addEventListener('click', function(){
            bottom_checkout_buttons.style.bottom = "0px";
        });

        if (freshdesk_icon && window.innerWidth < 1550) {
            bottom_checkout_buttons.style.paddingRight = '50px';
        }
    }

    /**
     * Switches the banner between being anchored to the anchor div on the footer,
     * or floating at the bottom of the window.
     * When the banner is anchored the close button should be hidden.
     *
     * @returns {void}
     */
    anchor_banner(): void {
        if (this.is_anchor_visible() || this.closed) {
            this.elements.anchor.classList.remove('fixed');
            this.elements.button_close.style.display = 'none';
        } else {
            this.elements.anchor.classList.add('fixed');
            this.elements.button_close.style.display = 'block';
        }
    }

    /**
     * Sets references to the DOM elements we need to interact with, and adds
     * event listeners to the window scrolling and the close button.
     *
     * @returns {void}
     */
    bind(): void {
        this.elements.button_close = document.getElementById('js-site-wide-banner-close');
        this.elements.anchor = document.getElementById('js-site-wide-banner-anchor');
        this.elements.footer = document.getElementById('tygh_footer');

        window.addEventListener('scroll', this.scroll.bind(this));

        this.elements.button_close.addEventListener('click', this.close.bind(this));
    }

    /**
     * Set the closed flag, and permanently anchor the banner.
     * Then store that this banner was closed in local storage so that future page loads don't
     * show the floating banner, but new banners will be displayed.
     *
     * @event
     * @returns {void}
     */
    close(): void {
        this.closed = true;
        this.anchor_banner();
        window.sessionStorage.setItem('site-wide-banner-closed', this.banner_id);
    }

    /**
     * When the anchor is visible the banner should be anchored. This is determined by calculating if the current
     * scrolled position is beyond the top of the footer where the anchor is.
     *
     * @return {boolean} true if the anchor is visible.
     */
    is_anchor_visible(): boolean {
        return (
            (window.innerHeight + window.pageYOffset) >= (document.body.offsetHeight - this.elements.footer.offsetHeight)
        );
    }

    /**
     * Checks the local storage for a previously closed banner, and compares the id to the current banner's.
     *
     * @return {boolean} true if the current banner is closed.
     */
    is_closed(): boolean {
        return this.banner_id === window.sessionStorage.getItem('site-wide-banner-closed');
    }

    /**
     * When an animation frame is provided, we can now check if the anchor is visible and anchor the banner.
     * Then set the flag that we're no longer waiting on a frame.
     *
     * @event
     * @return {void}
     */
    on_animation_frame(): void {
        this.anchor_banner();
        this.animating = false;
    }

    /**
     * When the window scrolls, we check the position of the window to determine if we need to hide or show the banner.
     * Tracking the animation and only updating on each animation frame's callback stops us from running the check and
     * anchoring the banner for every single scroll position (before the browser can even render the last change).
     *
     * @event
     * @return {void}
     */
    scroll(): void {
        // If we're still rendering a frame then don't do anything.
        if (this.animating) {
            return;
        }

        // Sets the flag that we're waiting on an animation frame, so no more scroll events ask for animation frames.
        this.animating = true;

        // Request a frame of animation, and provide the call back.
        window.requestAnimationFrame(this.on_animation_frame.bind(this));
    }
}

export function init() {
    if (document.getElementById('js-site-wide-banner')) {
        (() => new SiteWideBannerView())();
    }
}
